import React, { useContext, FC } from 'react';
import { Card } from '../../../../shared/card';
import {
  Span,
  Label,
  H2,
  H3,
  ListGroupRow,
  ListGroup,
} from '../../../../shared/typefaces/Typefaces.styles';
import {
  Container,
  Column,
  Heading,
  ColumnRight,
  SubHeading,
  Header,
  ListEnd,
  HeadingColumns,
  ListColumns,
  Dollar,
  StyledInput,
  StyledRightInput,
  UpdateRecommendedPriceLink,
} from './ProfitProjection.styles';
import { LayoutContext } from '../../../../../contexts/layoutContext';
import { theme } from '../../../../../styles/theme';
import { useFormContext, Controller } from 'react-hook-form';
import {
  convertGramsCleanly,
  convertCostCleanly,
  blurContent,
  convertPercentageCleanly,
  convertTimeMinutesCleanly,
} from '../../../../../utils/helper';
import { IRecipeDataResult } from '../../../../../utils/clientServerShared/getRecipeData.types';
import { restrictionPopupMessages } from '../../../../../utils/restrictionPopupMessages';
import { useRestrictionPopup } from '../../../../../utils/customHooks/useRestrictionPopup';
import { useShowCalcData } from '../../../../../utils/customHooks/useShowCalcData';
import { checkIfAccountIsNotComplete } from '../../../../../utils/helper/account';
import { overlayConstants } from '../../../../shared/layout/layoutOverlay/constants';
import {
  VenueQuery,
  VenueQueryVariables,
} from '../../../../../generated/graphql';
import * as ApolloClient from '@apollo/client';
import { getRecommendedPriceType } from '../../../results/utils';

const ProfitProjection: FC<{
  recipeData: IRecipeDataResult;
  venue?: ApolloClient.QueryResult<VenueQuery, VenueQueryVariables>;
}> = ({ recipeData, venue }) => {
  const { user, account, appWidth, toolTips, dispatch } = useContext(
    LayoutContext
  );
  const { errors, control } = useFormContext();
  const { showCalcData } = useShowCalcData();
  const showRestrictionPopup = useRestrictionPopup();
  const recommendedPrice =
    venue?.data?.venue?.userVenue?.venue.recommendedPrice;
  const targetFoodMargin =
    venue?.data?.venue?.userVenue?.venue.targetFoodMargin;
  const recommendedPrceType = getRecommendedPriceType(
    recommendedPrice,
    targetFoodMargin
  );

  const checkSalesPriceToolTip = () => {
    if (toolTips.salesPrice) {
      dispatch({
        type: 'SET_TOOL_TIPS',
        payload: { salesPrice: false },
      });
    }
  };
  const checkWeeklySalesToolTip = () => {
    if (toolTips.weeklySales) {
      dispatch({
        type: 'SET_TOOL_TIPS',
        payload: { weeklySales: false },
      });
    }
  };

  const handleRecipeRevenueUpgrade = () => {
    showRestrictionPopup(
      restrictionPopupMessages.cannotDisplayRecipeVersionProfit
    );
  };

  const updateVenue = () => {
    if (checkIfAccountIsNotComplete(user?.email, account?.type)) {
      dispatch({
        type: 'SET_OVERLAY',
        payload: overlayConstants.noAccountUpdateVenueDetails,
      });
    } else {
      dispatch({ type: 'SLIDER_PAGE', payload: 'venue' });
    }
  };

  return (
    <Container>
      <HeadingColumns colOneWidth="auto" colTwoWidth="auto">
        <Column>
          <Heading>
            <Controller
              as={<StyledInput />}
              type="number"
              name="salesPricePerServe"
              border={toolTips.salesPrice ? '' : 'noBorder'}
              control={control}
              errorText={
                errors.salesPricePerServe && errors.salesPricePerServe.message
              }
              startsymbol={<Dollar>$</Dollar>}
              defaultValue=""
              onClick={checkSalesPriceToolTip}
            />{' '}
            <Span className="profitHeaderSpan serve">Per serve</Span>
          </Heading>
          <SubHeading>
            <Label
              fontSize="small"
              className="profitHeaderSpan"
              fontWeight="semibold"
              color="faded"
            >
              Minimum Sale Price:
            </Label>{' '}
            <Span
              fontSize="small"
              className={`profitHeaderSpan ${
                !showCalcData && 'blur hasSeeIcon'
              }`}
              onClick={() => !showCalcData && handleRecipeRevenueUpgrade()}
            >
              {showCalcData
                ? convertCostCleanly(recipeData.recommendedPrice)
                : blurContent(convertCostCleanly(recipeData.recommendedPrice))}
            </Span>
          </SubHeading>
          <SubHeading>
            <Label
              fontSize="small"
              className="profitHeaderSpan"
              fontWeight="medium"
              color="faded"
            >
              Based on{' '}
              <Span color="grey" fontSize="small" fontWeight="medium">
                {recommendedPrceType}
              </Span>
            </Label>{' '}
            <UpdateRecommendedPriceLink
              color="primary"
              fontSize="small"
              onClick={updateVenue}
            >
              Change
            </UpdateRecommendedPriceLink>
          </SubHeading>
        </Column>
        {appWidth < theme.mQ.mobileM ? (
          <Column>
            <Heading>
              <Controller
                as={<StyledInput $noPadding $small />}
                type="number"
                name="weeklySalesPerServe"
                border={toolTips.weeklySales ? '' : 'noBorder'}
                control={control}
                errorText={
                  errors.weeklySalesPerServe &&
                  errors.weeklySalesPerServe.message
                }
                defaultValue=""
                onClick={checkWeeklySalesToolTip}
              />{' '}
              <Span className="profitHeaderSpan weeklySales">Weekly Sales</Span>
            </Heading>
            <SubHeading>
              <Label
                fontSize="small"
                className="profitHeaderSpan"
                fontWeight="semibold"
                color="faded"
              >
                Grams Per Serve:
              </Label>{' '}
              <Span fontSize="small" className="profitHeaderSpan">
                {convertGramsCleanly(recipeData.gramsPerServe)}
              </Span>
            </SubHeading>
            <SubHeading>
              <Label
                fontSize="small"
                className="profitHeaderSpan"
                fontWeight="semibold"
                color="faded"
              >
                Total Recipe Grams:
              </Label>{' '}
              <Span fontSize="small" className="profitHeaderSpan">
                {convertGramsCleanly(recipeData.totalGrams)}
              </Span>
            </SubHeading>
          </Column>
        ) : (
          <Column>
            <ColumnRight className="rightHeading">
              <Span className="profitHeaderSpan weeklySales">Weekly Sales</Span>{' '}
              <Controller
                as={<StyledRightInput />}
                type="number"
                name="weeklySalesPerServe"
                border={toolTips.weeklySales ? '' : 'noBorder'}
                control={control}
                errorText={
                  errors.weeklySalesPerServe &&
                  errors.weeklySalesPerServe.message
                }
                defaultValue=""
                onClick={checkWeeklySalesToolTip}
              />
            </ColumnRight>
            <SubHeading>
              <ColumnRight>
                <Label
                  fontSize="small"
                  className="profitHeaderSpan"
                  fontWeight="semibold"
                  color="faded"
                >
                  Grams Per Serve:
                </Label>
                <Span fontSize="small" className="profitHeaderSpan">
                  {convertGramsCleanly(recipeData.gramsPerServe)}
                </Span>
              </ColumnRight>
            </SubHeading>
            <SubHeading>
              <ColumnRight>
                <Label
                  fontSize="small"
                  className="profitHeaderSpan"
                  fontWeight="semibold"
                  color="faded"
                >
                  Total Recipe Grams:
                </Label>
                <Span fontSize="small" className="profitHeaderSpan">
                  {convertGramsCleanly(recipeData.totalGrams)}
                </Span>
              </ColumnRight>
            </SubHeading>
            <SubHeading>
              <ColumnRight>
                <Label
                  fontSize="small"
                  className="profitHeaderSpan"
                  fontWeight="semibold"
                  color="faded"
                >
                  Prep Time
                </Label>
                <Span fontSize="small" className="profitHeaderSpan">
                  {convertTimeMinutesCleanly(recipeData.totalPrepTime)}
                </Span>
              </ColumnRight>
            </SubHeading>
            <SubHeading>
              <ColumnRight>
                <Label
                  fontSize="small"
                  className="profitHeaderSpan"
                  fontWeight="semibold"
                  color="faded"
                >
                  Service Time:{' '}
                </Label>
                <Span fontSize="small" className="profitHeaderSpan">
                  {convertTimeMinutesCleanly(recipeData.totalTime)}
                </Span>
              </ColumnRight>
            </SubHeading>
          </Column>
        )}
      </HeadingColumns>

      <Card withBackground withCardLink>
        <Header colOneWidth="auto" colTwoWidth="auto" padding="small">
          <Column>
            <H2 fontSize="large">Profit & Projections</H2>
            <div>
              <Label
                fontSize="small"
                className="profitHeaderSpan"
                fontWeight="semibold"
                color="faded"
              >
                Version:
              </Label>{' '}
              <Span fontSize="small" className="profitHeaderSpan">
                {recipeData.versionName}
              </Span>
            </div>
            {appWidth <= theme.mQ.mobileM && (
              <div>
                <Label
                  fontSize="small"
                  fontWeight="semibold"
                  color="faded"
                  className="profitHeaderSpan"
                >
                  Recipe Version Serves
                </Label>{' '}
                <Span fontSize="small" className="profitHeaderSpan">
                  {recipeData.serves}
                </Span>
              </div>
            )}
          </Column>
          {appWidth > theme.mQ.mobileM && (
            <Column className="cardHeaderItemRight">
              <H2 fontSize="large" className="inlineHeaderTitle" color="grey">
                {recipeData.serves}
              </H2>
              <Label
                fontSize="small"
                fontWeight="semibold"
                color="faded"
                className="profitHeaderSpan"
              >
                Recipe Version Serves
              </Label>
            </Column>
          )}
        </Header>

        <ListColumns colOneWidth="auto" colTwoWidth="auto" padding="small">
          <div>
            <H3 fontWeight="semibold" color="black">
              Gross Profit
            </H3>
            <ListGroup>
              <ListGroupRow colOneWidth="130px" colTwoWidth="auto">
                <Label color="grey">per Serve</Label>
                <Span
                  className={`${!showCalcData && 'blur hasSeeIcon'}`}
                  onClick={() => !showCalcData && handleRecipeRevenueUpgrade()}
                >
                  {showCalcData
                    ? convertCostCleanly(recipeData.profitPerServe)
                    : blurContent(
                        convertCostCleanly(recipeData.recommendedPrice)
                      )}
                </Span>
              </ListGroupRow>
              <ListGroupRow colOneWidth="130px" colTwoWidth="auto">
                <Label color="grey">per Month</Label>
                <Span
                  className={`${!showCalcData && 'blur hasSeeIcon'}`}
                  onClick={() => !showCalcData && handleRecipeRevenueUpgrade()}
                >
                  {showCalcData
                    ? convertCostCleanly(recipeData.profitPerMonth)
                    : blurContent(
                        convertCostCleanly(recipeData.profitPerMonth)
                      )}
                </Span>
              </ListGroupRow>
              <ListGroupRow colOneWidth="130px" colTwoWidth="auto">
                <Label color="grey">per Year</Label>
                <Span
                  className={`${!showCalcData && 'blur hasSeeIcon'}`}
                  onClick={() => !showCalcData && handleRecipeRevenueUpgrade()}
                >
                  {showCalcData
                    ? convertCostCleanly(recipeData.profitPerYear)
                    : blurContent(convertCostCleanly(recipeData.profitPerYear))}
                </Span>
              </ListGroupRow>
              <ListGroupRow colOneWidth="130px" colTwoWidth="auto">
                <Label color="grey">per Minute</Label>
                <Span
                  className={`${!showCalcData && 'blur hasSeeIcon'}`}
                  onClick={() => !showCalcData && handleRecipeRevenueUpgrade()}
                >
                  {showCalcData
                    ? convertCostCleanly(recipeData.profitPerMin)
                    : blurContent(convertCostCleanly(recipeData.profitPerMin))}
                </Span>
              </ListGroupRow>
            </ListGroup>
            <ListEnd>
              <ListGroupRow colOneWidth="130px" colTwoWidth="auto">
                <Label color="grey">Recipe Profit</Label>
                <Span
                  className={`${!showCalcData && 'blur hasSeeIcon'}`}
                  onClick={() => !showCalcData && handleRecipeRevenueUpgrade()}
                >
                  {showCalcData
                    ? convertCostCleanly(recipeData.recipeProfit)
                    : blurContent(convertCostCleanly(recipeData.recipeProfit))}
                </Span>
              </ListGroupRow>
              <ListGroupRow colOneWidth="130px" colTwoWidth="auto">
                <Label color="grey">Recipe Margin</Label>
                <Span
                  className={`${!showCalcData && 'blur hasSeeIcon'}`}
                  onClick={() => !showCalcData && handleRecipeRevenueUpgrade()}
                >
                  {showCalcData
                    ? convertPercentageCleanly(
                        recipeData.profitMarginPercentage
                      )
                    : blurContent(
                        convertPercentageCleanly(
                          recipeData.profitMarginPercentage
                        )
                      )}
                </Span>
              </ListGroupRow>
            </ListEnd>
          </div>
          <div>
            <H3 fontWeight="semibold" color="black">
              Net Revenue
            </H3>
            <ListGroup>
              <ListGroupRow colOneWidth="100px" colTwoWidth="auto">
                <Label color="grey">per Serve</Label>
                <Span>{convertCostCleanly(recipeData.salesPricePerServe)}</Span>
              </ListGroupRow>
              <ListGroupRow colOneWidth="100px" colTwoWidth="auto">
                <Label color="grey">per Month</Label>
                <Span>{convertCostCleanly(recipeData.revenuePerMonth)}</Span>
              </ListGroupRow>
              <ListGroupRow colOneWidth="100px" colTwoWidth="auto">
                <Label color="grey">per Year</Label>
                <Span>{convertCostCleanly(recipeData.revenuePerYear)}</Span>
              </ListGroupRow>
              {appWidth > theme.mQ.mobileM && <ListGroupRow />}
            </ListGroup>
            <ListEnd>
              <ListGroupRow colOneWidth="100px" colTwoWidth="auto">
                <Label color="grey">Total Recipe</Label>
                <Span>{convertCostCleanly(recipeData.recipeRevenue)}</Span>
              </ListGroupRow>
            </ListEnd>
          </div>
        </ListColumns>
        <ListColumns colOneWidth="auto" colTwoWidth="auto" padding="small">
          <div>
            <H3 fontWeight="semibold" color="black">
              Mark Up
            </H3>
            <ListGroup>
              <ListGroupRow colOneWidth="130px" colTwoWidth="auto">
                <Label color="grey">Food Markup</Label>
                <Span
                  className={`${!showCalcData && 'blur hasSeeIcon'}`}
                  onClick={() => !showCalcData && handleRecipeRevenueUpgrade()}
                >
                  {showCalcData
                    ? convertPercentageCleanly(
                        recipeData.ingredientMarkupPercentage
                      )
                    : blurContent(
                        convertPercentageCleanly(
                          recipeData.ingredientMarkupPercentage
                        )
                      )}
                </Span>
              </ListGroupRow>
              <ListGroupRow colOneWidth="130px" colTwoWidth="auto">
                <Label color="grey">Recipe Markup</Label>
                <Span
                  className={`${!showCalcData && 'blur hasSeeIcon'}`}
                  onClick={() => !showCalcData && handleRecipeRevenueUpgrade()}
                >
                  {showCalcData
                    ? convertPercentageCleanly(recipeData.markupPercentage)
                    : blurContent(
                        convertPercentageCleanly(recipeData.markupPercentage)
                      )}
                </Span>
              </ListGroupRow>
            </ListGroup>
          </div>
          <div>
            <H3 fontWeight="semibold" color="black">
              Recommendations
            </H3>
            <ListGroup>
              <ListGroupRow colOneWidth="140px" colTwoWidth="auto">
                <Label color="grey">Coming soon</Label>
              </ListGroupRow>
            </ListGroup>
          </div>
        </ListColumns>
      </Card>
    </Container>
  );
};

export default ProfitProjection;
